// En plats position (b5): koordinaterna ur det ägaren skriver eller klistrar in, utan någon
// extern tjänst. Det går med lat och lon i grader, till exempel "61.315, 12.264", eller med en
// länk från en karta:
//   - Google Maps: platsens markering (!3d…!4d…), q= eller ll=, annars kartans mitt (@lat,lon);
//   - Apple Kartor: ll=, q= eller sll=;
//   - OpenStreetMap: mlat= och mlon=, eller #map=zoom/lat/lon;
//   - Bing: cp=lat~lon;
//   - geo:lat,lon.
// En kortlänk (maps.app.goo.gl, goo.gl/maps, apple.co) går inte att läsa utan att fråga
// kartans tjänst, så den läses inte.

import type { Position } from "./signaler.ts";

/** Sant om positionen är giltiga grader. */
export function arPosition(p: unknown): p is Position {
  if (typeof p !== "object" || p === null) return false;
  const { lat, lon } = p as Record<string, unknown>;
  return (
    typeof lat === "number" &&
    typeof lon === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lon) <= 180
  );
}

/** Sant om texten är en kortlänk till en karta, som inte går att läsa utan kartans tjänst. */
export function arKortlank(text: string): boolean {
  return /^(https?:\/\/)?(maps\.app\.goo\.gl|goo\.gl\/maps|apple\.co|g\.co\/kgs)\//i.test(
    text.trim(),
  );
}

const TAL = String.raw`[-+]?\d{1,3}(?:\.\d+)?`;

/** "lat,lon" eller "lat lon", som i en kartlänk. */
function par(text: string | null | undefined): Position | null {
  if (!text) return null;
  const m = new RegExp(`^\\s*(?:loc:)?\\s*(${TAL})\\s*[, ]\\s*(${TAL})\\b`).exec(text);
  if (!m) return null;
  const p = { lat: Number(m[1]), lon: Number(m[2]) };
  return arPosition(p) ? p : null;
}

/** Grader med väderstreck, till exempel "57.6697° N, 11.8456° E" eller "61,315 N 12,264 Ö". */
function medVaderstreck(text: string): Position | null {
  const g = String.raw`(\d{1,3}(?:[.,]\d+)?)\s*°?\s*`;
  const m = new RegExp(`^\\s*${g}([NS])\\s*[,;]?\\s*${g}([EWÖOV])\\s*$`, "i").exec(text);
  if (!m) return null;
  const tal = (s: string | undefined) => Number((s ?? "").replace(",", "."));
  const lat = tal(m[1]) * (m[2]?.toUpperCase() === "S" ? -1 : 1);
  const lon = tal(m[3]) * (/[WV]/i.test(m[4] ?? "") ? -1 : 1);
  const p = { lat, lon };
  return arPosition(p) ? p : null;
}

/** Två tal: "61.315, 12.264", "61.315 12.264", eller med decimalkomma "61,315; 12,264". */
function tvaTal(text: string): Position | null {
  const t = text.trim();
  const punkt = new RegExp(`^(${TAL})\\s*[,; ]\\s*(${TAL})$`).exec(t);
  const komma = /^([-+]?\d{1,3}(?:,\d+)?)\s*(?:;|\s)\s*([-+]?\d{1,3}(?:,\d+)?)$/.exec(t);
  const m = punkt ?? komma;
  if (!m) return null;
  const p = {
    lat: Number((m[1] ?? "").replace(",", ".")),
    lon: Number((m[2] ?? "").replace(",", ".")),
  };
  return arPosition(p) ? p : null;
}

/** Koordinaterna i en länk från en karta, eller null. */
function urLank(text: string): Position | null {
  const t = text.trim();
  if (/^geo:/i.test(t)) return par(t.slice(4).split(/[;?]/)[0]);
  if (!URL.canParse(t)) return null;
  const url = new URL(t);
  let hela = url.href;
  try {
    hela = decodeURIComponent(hela);
  } catch {}
  // Google: platsens markering går före kartans mitt.
  const markering = new RegExp(`!3d(${TAL})!4d(${TAL})`).exec(hela);
  if (markering) {
    const p = { lat: Number(markering[1]), lon: Number(markering[2]) };
    if (arPosition(p)) return p;
  }
  const q = url.searchParams;
  const mlat = q.get("mlat");
  const mlon = q.get("mlon");
  if (mlat && mlon) {
    const p = { lat: Number(mlat), lon: Number(mlon) };
    if (arPosition(p)) return p;
  }
  for (const namn of ["q", "query", "ll", "destination", "daddr", "center", "sll"]) {
    const p = par(q.get(namn));
    if (p) return p;
  }
  const cp = q.get("cp");
  if (cp) {
    const p = par(cp.replace("~", ","));
    if (p) return p;
  }
  const mitt = new RegExp(`/@(${TAL}),(${TAL})`).exec(hela);
  if (mitt) {
    const p = { lat: Number(mitt[1]), lon: Number(mitt[2]) };
    if (arPosition(p)) return p;
  }
  const osm = new RegExp(`#map=\\d+(?:\\.\\d+)?/(${TAL})/(${TAL})`).exec(hela);
  if (osm) {
    const p = { lat: Number(osm[1]), lon: Number(osm[2]) };
    if (arPosition(p)) return p;
  }
  return null;
}

/**
 * Positionen ur det ägaren skriver eller klistrar in: koordinater eller en länk från en karta.
 * Null om inga giltiga koordinater går att läsa.
 */
export function tolkaPosition(text: string): Position | null {
  if (!text.trim() || arKortlank(text)) return null;
  return tvaTal(text) ?? medVaderstreck(text) ?? urLank(text);
}
